import { onBeforeUnmount, watch, type Ref } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'details > summary:first-of-type',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

function isVisible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  const style = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
  if (style && (style.visibility === 'hidden' || style.display === 'none')) return false;
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter(isVisible);
}

export interface UseFocusTrapOptions {
  /** Вернуть фокус на исходный элемент после отключения. Default — true. */
  restoreFocus?: boolean;
  /** Автоматически поставить фокус на первый интерактив при активации. Default — true. */
  autoFocus?: boolean;
}

export interface UseFocusTrapReturn {
  readonly activate: () => void;
  readonly deactivate: () => void;
}

/**
 * Замыкает клавиатурный Tab/Shift+Tab внутри контейнера.
 *
 * Активируется вручную через `activate()` либо автоматически через
 * `watch(active)`. При unmount — безопасно освобождает слушатель и
 * возвращает фокус на сохранённый `activeElement`.
 */
export function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  active?: Ref<boolean>,
  options: UseFocusTrapOptions = {},
): UseFocusTrapReturn {
  const { restoreFocus = true, autoFocus = true } = options;

  let isActive = false;
  let previouslyFocused: HTMLElement | null = null;

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const container = containerRef.value;
    if (!container) return;

    const focusable = getFocusable(container);
    if (focusable.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement as HTMLElement | null;
    const withinContainer = current ? container.contains(current) : false;

    if (event.shiftKey) {
      if (!withinContainer || current === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (!withinContainer || current === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function activate(): void {
    if (isActive) return;
    if (typeof document === 'undefined') return;
    const container = containerRef.value;
    if (!container) return;
    previouslyFocused = document.activeElement as HTMLElement | null;
    document.addEventListener('keydown', handleKeydown, true);
    if (autoFocus) {
      queueMicrotask(() => {
        const focusable = getFocusable(container);
        const target = focusable[0] ?? container;
        if (!container.contains(document.activeElement)) {
          target.focus();
        }
      });
    }
    isActive = true;
  }

  function deactivate(): void {
    if (!isActive) return;
    if (typeof document === 'undefined') return;
    document.removeEventListener('keydown', handleKeydown, true);
    isActive = false;
    if (restoreFocus && previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  if (active) {
    watch(
      active,
      (value) => {
        if (value) activate();
        else deactivate();
      },
      { flush: 'post' },
    );
  }

  onBeforeUnmount(deactivate);

  return { activate, deactivate };
}
